import { camelCase } from 'change-case';
import type { LoadOptions } from 'devextreme/data';
import { getDate } from '@/utils/moment';
import type { AccountFilterFormatters } from '~/types';

export const formatters: Record<keyof AccountFilterFormatters, string> = {
  id: 'id',
  status: 'status',
  type: 'type',
  identity: 'identity.name',
  identityEmail: 'identity.email',
  profiles: 'profiles.type'
};

export const formatFilter = (column: keyof AccountFilterFormatters, value: string): string => {
  const sanitizedValue = value.replace(/,/g, ' ');
  return formatters[column] ? `${formatters[column]}:${sanitizedValue}` : sanitizedValue;
};

export const processFilter = (filter: unknown): unknown[] => {
  if (
    Array.isArray(filter) &&
    typeof filter[0] === 'string' &&
    (filter as unknown[])[1] &&
    typeof (filter as unknown[])[1] === 'string' &&
    (filter as unknown[]).length >= 3
  ) {
    return [filter];
  }
  if (Array.isArray(filter)) {
    return (filter as unknown[])
      .filter((item) => item !== 'and' && item !== 'or' && item !== '!')
      .flatMap((item) => processFilter(item));
  }
  if (typeof filter === 'object' && filter !== null) {
    return Object.values(filter).flatMap((item) => processFilter(item));
  }
  return [];
};

export const formatSingleFilter = (item: unknown): string[] => {
  if (Array.isArray(item)) {
    const parts = item as unknown[];
    if (parts[0] === 'date') {
      const [, from, to] = parts as [string, string | undefined, string | undefined];
      if (from && to) return [from, to];
      return [];
    }
    return [
      formatFilter(
        camelCase(parts[0] as string) as keyof AccountFilterFormatters,
        parts[2] as string
      )
    ];
  }
  return [];
};

export const getFilters = (loadOptions: LoadOptions): string | undefined => {
  if (!loadOptions.filter) return undefined;

  const flattened = processFilter(loadOptions.filter)
    .flatMap((node) => formatSingleFilter(node))
    .filter(Boolean) as string[];

  const groupedValues: Record<string, string[]> = {};
  flattened.forEach((token) => {
    const [field, value] = token.split(':');
    if (!field || value === undefined || value === '') return;
    (groupedValues[field] ||= []).push(value);
  });

  const merged = Object.entries(groupedValues).map(
    ([field, values]) => `${field}:${values.join('|')}`
  );
  return merged.length ? merged.join(',') : undefined;
};

export const normalizeFilters = (
  loadOptions: LoadOptions,
  dateRangeValue: string[]
): { filtersArr: string[]; identityNameValue?: string } => {
  let filtersArr = (getFilters(loadOptions)?.split(',') ?? []).filter(Boolean);

  if (!filtersArr.some((f) => f.startsWith('createdAtFrom:'))) {
    filtersArr.push(
      `createdAtFrom:${getDate(dateRangeValue[0], 'DD-MM-YYYY').format('YYYY-MM-DD')}`,
      `createdAtTo:${getDate(dateRangeValue[1], 'DD-MM-YYYY').format('YYYY-MM-DD')}`
    );
  }

  const identityNameFilter = filtersArr.find((filter) => filter.startsWith('identity.name:'));
  const identityNameValue = identityNameFilter ? identityNameFilter.split(':')[1] : undefined;
  if (identityNameFilter) {
    filtersArr = filtersArr.filter((filter) => !filter.startsWith('identity.name:'));
  }

  return { filtersArr, identityNameValue };
};

export const updateFilter = (
  filters: string[],
  field: string,
  rawValue: string | undefined,
  isMulti: boolean = false
): string[] => {
  const updated = filters.filter((filterEntry) => !filterEntry.startsWith(`${field}:`));
  if (rawValue) {
    const trimmedValue = rawValue.trim();
    if (trimmedValue) {
      let normalizedValue = trimmedValue;
      if (isMulti) {
        normalizedValue = trimmedValue
          .split(',')
          .map((segment) => segment.trim())
          .filter(Boolean)
          .join('|');
      }
      if (normalizedValue) updated.push(`${field}:${normalizedValue}`);
    }
  }
  return updated;
};
